/**
 * Extension points on the meeting card. Phase 2 (TRUS-692): the host's guest
 * link — create, copy, revoke — when the org allows guest links for this
 * meeting (`guest_link_enabled`). One link is active per meeting; creating a
 * new one stops the old one working. The link is shown only right after it is
 * created (the server keeps a hash, not the link).
 */

import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Copy, Link2, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { meetingPhase, type GuestLink, type Meeting } from '@/services/meetingsApi';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { formatMeetingRange } from './meetingTime';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { copyToClipboard, useMeetingCache, useMeetingsApi } from './useMeetings';

export function GuestLinkSlot({ meeting }: { meeting: Meeting }) {
  const api = useMeetingsApi();
  const phase = meetingPhase(meeting);
  const isHost = meeting.can_edit || meeting.i_am_host;
  if (api.kind !== 'staff' || !meeting.guest_link_enabled || !isHost) return null;
  if (phase === 'ended' || phase === 'cancelled') return null;
  return <GuestLinkControls meeting={meeting} />;
}

export function GuestLinkControls({ meeting }: { meeting: Meeting }) {
  const { t, language } = useAppTranslation();
  const tz = useUserTimezone(meeting.org_timezone);
  const api = useMeetingsApi();
  const update = useMeetingCache();
  const [link, setLink] = useState<GuestLink | null>(null);
  const [revoked, setRevoked] = useState(false);
  const active = !revoked && (!!link || meeting.guest_link_active);
  const expiresAt = link?.expires_at ?? (revoked ? null : meeting.guest_link_expires_at);

  const create = useMutation({
    mutationFn: () => api.createGuestLink(meeting.id),
    onSuccess: (res) => {
      setLink(res);
      setRevoked(false);
      update({ ...meeting, guest_link_active: true, guest_link_expires_at: res.expires_at });
    },
    onError: (err: unknown) =>
      toast.error(t('messaging.meetings.guestLinkFailed', 'Could not create a guest link'), {
        description: (err as Error)?.message,
      }),
  });
  const revoke = useMutation({
    mutationFn: () => api.revokeGuestLink(meeting.id),
    onSuccess: () => {
      setLink(null);
      setRevoked(true);
      update({ ...meeting, guest_link_active: false, guest_link_expires_at: null });
      toast.success(t('messaging.meetings.guestLinkRevoked', 'Guest link turned off. It no longer works.'));
    },
    onError: (err: unknown) =>
      toast.error(t('messaging.meetings.guestLinkRevokeFailed', 'Could not turn off the guest link'), {
        description: (err as Error)?.message,
      }),
  });

  const copy = async () => {
    if (!link) return;
    const ok = await copyToClipboard(link.url);
    if (ok) toast.success(t('messaging.meetings.guestLinkCopied', 'Guest link copied'));
    else toast.error(t('messaging.meetings.copyFailed', 'Could not copy the link'));
  };

  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-border p-2" data-testid={`guest-link-${meeting.id}`}>
      <div className="flex items-center gap-1.5 text-xs font-medium">
        <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
        {t('messaging.meetings.guestLinkTitle', 'Guest link')}
      </div>
      <p className="text-xs text-muted-foreground">
        {t(
          'messaging.meetings.guestLinkHint',
          'Anyone with the link can ask to join without an account. You let each guest in from the call.',
        )}
      </p>
      {link && (
        <div className="flex items-center gap-1.5">
          <Input
            readOnly
            value={link.url}
            onFocus={(e) => e.currentTarget.select()}
            aria-label={t('messaging.meetings.guestLinkTitle', 'Guest link')}
            className="h-8 text-xs"
            data-testid="guest-link-url"
          />
          <Button type="button" size="sm" variant="outline" className="gap-1" onClick={() => void copy()} data-testid="guest-link-copy">
            <Copy className="h-3.5 w-3.5" />
            {t('messaging.meetings.copyLink', 'Copy link')}
          </Button>
        </div>
      )}
      {active && expiresAt && (
        <p className="text-xs text-muted-foreground" data-testid="guest-link-expires">
          {t('messaging.meetings.guestLinkExpires', 'Works until {{time}}.', { time: formatMeetingRange(expiresAt, null, language, tz) })}
        </p>
      )}
      {active && !link && (
        <p className="text-xs text-muted-foreground">
          {t('messaging.meetings.guestLinkActiveHidden', 'A guest link is active. Create a new one to copy it again.')}
        </p>
      )}
      <div className="flex flex-wrap gap-1.5">
        <Button
          type="button"
          size="sm"
          variant={active ? 'outline' : 'default'}
          className="gap-1"
          disabled={create.isPending}
          onClick={() => create.mutate()}
          title={active ? t('messaging.meetings.guestLinkNewHint', 'The old link stops working.') : undefined}
          data-testid="guest-link-create"
        >
          {create.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : active ? (
            <RefreshCw className="h-3.5 w-3.5" />
          ) : (
            <Link2 className="h-3.5 w-3.5" />
          )}
          {active ? t('messaging.meetings.guestLinkNew', 'New link') : t('messaging.meetings.guestLinkCreate', 'Create guest link')}
        </Button>
        {active && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="gap-1 text-red-600 hover:text-red-700 dark:text-red-400"
            disabled={revoke.isPending}
            onClick={() => revoke.mutate()}
            data-testid="guest-link-revoke"
          >
            {revoke.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            {t('messaging.meetings.guestLinkRevoke', 'Turn off link')}
          </Button>
        )}
      </div>
    </div>
  );
}
